import { createPushToken as _createPushToken } from '@griply/services';
import { FcmTokenType, FirestoreCollections, Locales } from '@griply/types';
import { getDeviceTimeZone, getOffsetTimeZone } from '@griply/utils';
import { doc, getFirestore, serverTimestamp, setDoc } from 'firebase/firestore';
import { version } from 'package.json';
import { getShellTimeZone } from 'shared/services/electron/get-shell-time-zone';
import {
  FirestoreWriteOp,
  logFirestoreWriteError,
} from 'shared/services/error-tracking';
import { setPushNotificationTokenDate } from 'shared/services/local-storage';
import { ID } from 'shared/types/id';
import { isProductionEnvironment } from 'shared/utils/is-environment';

import { getUser } from './get-user';
import { setPushNotificationToken } from './helpers';

const getSavedTimeZone = async (): Promise<string | undefined> => {
  try {
    return (await getUser())?.settings?.timezone || undefined;
  } catch {
    // getUser reports its own read failures
    return undefined;
  }
};

// Backend sends filter on this zone, so it is never left out. The shell asks the OS itself, which covers the Windows
// zones Chromium's ICU cannot name; the later steps cover shells that predate it and zones nothing can name.
const getPushTokenTimeZone = async (): Promise<string> =>
  (await getShellTimeZone()) ||
  getDeviceTimeZone() ||
  (await getSavedTimeZone()) ||
  getOffsetTimeZone();

export const createPushToken = async (
  uid: ID,
  token: string,
  locale: Locales = Locales.EnUS,
) => {
  if (!isProductionEnvironment) {
    // eslint-disable-next-line no-console -- dev only
    console.log('create push token - ', { token, uid });
  }
  if (!uid || !token) {
    return;
  }

  const timezone = await getPushTokenTimeZone();

  _createPushToken({
    userId: uid,
    token,
    type: FcmTokenType.Desktop,
    // the shell runs the same web build, so the bridge is the only thing that tells the two apart
    platform: window.electron ? 'electron' : 'web',
    version,
    timezone,
    language: locale,
    db: getFirestore(),
    doc,
    setDoc,
    serverTimestamp,
    onWriteError: (error) =>
      logFirestoreWriteError({
        op: FirestoreWriteOp.CreatePushToken,
        error,
        docPath: `${FirestoreCollections.FcmTokens}/${token}`,
        uid,
      }),
  }).catch((error) =>
    logFirestoreWriteError({
      op: FirestoreWriteOp.CreatePushToken,
      error,
      docPath: `${FirestoreCollections.FcmTokens}/${token}`,
      uid,
    }),
  );

  setPushNotificationToken(uid, token);
  setPushNotificationTokenDate(uid, new Date());
};
